import { queryClient, QueryKeys } from "@/api/core";
import {
  type Mention,
  mentionSchema,
  type Account,
  type Status,
} from "@/schemas";
import { isIntegerId } from "@/utils/numbers";

import type { IntlShape } from "react-intl";

/** Get the initial visibility of media attachments from user settings. */
export const defaultMediaVisibility = <
  T extends { reblog: T | string | null } & Pick<
    Status,
    "visibility" | "sensitive"
  >,
>(
  status: T | undefined | null,
  displayMedia: string,
): boolean => {
  if (!status) return false;
  status = getActualStatus(status);

  const isUnderReview = status.visibility === "self";

  if (isUnderReview) {
    return false;
  }

  return (
    (displayMedia !== "hide_all" && !status.sensitive) ||
    displayMedia === "show_all"
  );
};

/** Sanitize status text for use with screen readers. */
export const textForScreenReader = (
  intl: IntlShape,
  status: Pick<Status, "account" | "hidden" | "search_index" | "created_at">,
  rebloggedByText?: string,
  boostedText?: string,
): string => {
  const { account } = status;
  if (!account || typeof account !== "object") return "";

  const displayName = account.display_name;

  const values = [
    displayName.length === 0 ? account.acct.split("@")[0] : displayName,
    status.hidden ? "" : status.search_index,
    intl.formatDate(status.created_at, {
      hour: "2-digit",
      minute: "2-digit",
      month: "short",
      day: "numeric",
    }),
    account.acct,
  ];

  // Boosted prefix leads so AT users hear context before content. The wrapper's
  // aria-label fully replaces descendant text in focus mode, so any sr-only
  // span on the badge would be silent — must live here to be announced.
  if (boostedText) {
    values.unshift(boostedText);
  }

  if (rebloggedByText) {
    values.push(rebloggedByText);
  }

  return values.join(", ");
};

/** Get reblogged status if any, otherwise return the original status. */
export const getActualStatus = <T extends { reblog: T | string | null }>(
  status?: T,
): T => {
  if (status?.reblog && typeof status?.reblog === "object") {
    return status.reblog;
  } else {
    return status!;
  }
};

export const getStatusMentions = (status: Status) => {
  const mentions: Mention[] = [];
  // first, we are taking account from in_reply_to
  if (status.in_reply_to) {
    mentions.push(mentionSchema.parse(status.in_reply_to.account));
    // if there is no in_reply_to status, we check account cache
    // with in_reply_to_account_id
  } else if (status.in_reply_to_account_id) {
    const acc = queryClient.getQueryData(
      QueryKeys.accounts.account(status.in_reply_to_account_id),
    ) as Account | undefined;
    if (acc) mentions.push(mentionSchema.parse(acc));
  }

  // exclude same account from status.mentions
  const statusMentions = status.mentions.filter((account) => {
    if (!mentions[0]) return true;

    return account.id !== mentions[0].id;
  });

  return [...mentions, ...statusMentions];
};
